import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerUploadPortal = () => {
  const { token } = useParams();
  const [bio, setBio] = useState('');
  const [photo, setPhoto] = useState(null);
  const [consent, setConsent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('bio', bio);
      if (photo) formData.append('photo', photo);

      const response = await fetch(`${BACKEND_URL}/api/upload/asset/${token}`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        alert('Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#0A0A0A] border border-[#A2FF00] rounded-sm p-8 text-center">
          <CheckCircle className="w-16 h-16 text-[#A2FF00] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Upload Successful!
          </h2>
          <p className="text-gray-400">Your company description and logo have been submitted.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-[#0A0A0A] border border-white/10 rounded-sm p-8">
        <div className="text-center mb-8">
          <Upload className="w-12 h-12 text-[#A2FF00] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Upload Your Assets
          </h2>
          <p className="text-gray-400">Please provide your company description and logo for the upcoming event.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="upload-form">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Company Description
            </label>
            <p className="text-xs text-gray-500 mb-2">This is how your company will be presented on our website</p>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={2000}
              rows={6}
              required
              data-testid="bio-input"
              className="w-full bg-[#111] border border-white/20 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] rounded-sm text-white p-3"
              placeholder="Tell us about your company..."
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {bio.length}/2000
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Logo
            </label>
            <p className="text-xs text-gray-500 mb-2">Add your company logo in JPEG, WebP or PNG</p>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setPhoto(e.target.files[0])}
              required
              data-testid="photo-input"
              className="w-full bg-[#111] border border-white/20 focus:border-[#A2FF00] rounded-sm text-white p-3 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[#A2FF00] file:text-black file:font-bold"
            />
          </div>

          <div className="bg-[#111] border border-white/10 rounded-sm p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                required
                data-testid="consent-checkbox"
                className="w-5 h-5 mt-0.5 rounded border-white/20 bg-[#0A0A0A] text-[#A2FF00] focus:ring-[#A2FF00] focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-sm text-gray-300 leading-relaxed">
                I understand that my information will be used to promote the event, I understand video from the event will be used on the QSECDEF platform and social media accounts.
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={uploading || !consent}
            data-testid="submit-button"
            className="w-full bg-[#A2FF00] text-black py-3 font-bold uppercase tracking-wider rounded-sm hover:bg-[#8EFF00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Uploading...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CustomerUploadPortal;
